import React, { useContext, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { BsArrowDown } from "react-icons/bs";
import { useSelector, useDispatch } from "react-redux";
import { AuthContext, funcionalidades } from "../../../context/AuthContext";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import SelectInput from "../../../components/UI/SelectInput/SelectInput";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import useColumns from "./hooks/useColumns";
import PatDetalhesExpandido from "./components/PatDetalhesExpandido";
import { formatarData } from "../../../components/Formatters/DateTime";
import constants from "../../Constants";
import { patSearchActions } from "../../../redux/pesquisaAvancadaPat";

const validationSchema = Yup.object().shape({
  NuPat: Yup.string().default(""),
  nuProtocolo: Yup.string().default(""),
  nuOs: Yup.string().default(""),
  identificador: Yup.string()
    .default("")
    .test("identificador", "Identificador inválido", (value) => {
      const id = value.replace(/\D/g, "");
      if (id.length !== 14 && id.length !== 11 && id.length !== 9 && id.length !== 0) return false;
      return true;
    }),
  urtProcessante: Yup.string().default(""),
  sqSituacao: Yup.string().default(""),
  motivoBaixa: Yup.string().default(""),
  detalheBaixa: Yup.string().default(""),
  dtInicio: Yup.string().default(""),
  dtFim: Yup.string().default(""),
});

const situacaoPat = [
  { key: "0", value: "", text: "Selecione" },
  { key: "1", value: "P", text: "Aberto" },
  { key: "2", value: "C", text: "Cancelado" },
  { key: "3", value: "B", text: "Baixado" },
  { key: "4", value: "A", text: "Aguardando Checklist" },
  { key: "5", value: "E", text: "Em ação fiscal" },
  { key: "6", value: "D", text: "PDA" },
];

const motivoBaixaOptions = [
  { key: "0", value: "", text: "Selecione" },
  { key: "1", value: "PP", text: "Pagamento/Parcelamento" },
  { key: "2", value: "DA", text: "Envio pra Dívida Ativa" },
  { key: "3", value: "OU", text: "Outros" },
  { key: "4", value: "SS", text: "Simples Nacional - Sem Efeito" },
  { key: "5", value: "SE", text: "Simples Nacional - Efetivado" },
];

const detalheBaixaMap = {
  PP: [
    { key: "0", value: "", text: "Selecione" },
    { key: "1", value: "PG", text: "Pagamento" },
    { key: "2", value: "PC", text: "Parcelamento" },
  ],
  DA: [
    { key: "0", value: "", text: "Selecione" },
    { key: "1", value: "DP", text: "Decisão Definitiva Procedente" },
    { key: "2", value: "PP", text: "Decisão Definitiva Procedente em Parte" },
  ],
  OU: [
    { key: "0", value: "", text: "Selecione" },
    { key: "1", value: "AJ", text: "Ação Judicial" },
    { key: "2", value: "AN", text: "Anistia" },
    { key: "3", value: "CO", text: "Compensação" },
    { key: "4", value: "DA", text: "Despacho Anulatório de PAT" },
    { key: "5", value: "DM", text: "Doação de Mercadorias" },
    { key: "6", value: "RE", text: "Remissão" },
    { key: "7", value: "DI", text: "Decisão Definitiva Improcedente" },
    { key: "8", value: "DN", text: "Decisão Definitiva - AI Nulo" },
    { key: "9", value: "OU", text: "Outros" },
  ],
  SS: [
    { key: "0", value: "", text: "Selecione" },
    { key: "1", value: "PG", text: "Pagamento" },
    { key: "2", value: "PC", text: "Parcelamento" },
    { key: "3", value: "RC", text: "Regularização da situação cadastral" },
    { key: "4", value: "ID", text: "Impugnação deferida" },
    { key: "5", value: "AN", text: "Anulação" },
    { key: "6", value: "OS", text: "Outros" },
  ],
  SE: [
    { key: "0", value: "", text: "Selecione" },
    { key: "1", value: "AP", text: "Ausência de pag/parc e de impugnação" },
    { key: "2", value: "AR", text: "Ausência de regularização da situação cadastral" },
    { key: "3", value: "II", text: "Impugnação indeferida" },
    { key: "4", value: "OE", text: "Outros" },
  ],
};

const ConsolidacaoDebitosFiscais = ({ location, history }) => {
  const dispatch = useDispatch();
  const { patSearchParameters, searchResults } = useSelector((state) => state.patSearch);
  const [rows, setRows] = useState(searchResults);
  const [urtOptions, setUrtOptions] = useState([{ key: "0", value: "", text: "Selecione" }]);
  const { user } = useContext(AuthContext);
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();
  const { request: requestUrt, data: dataUrt } = useApiClient();
  const columns = useColumns();
  const { isAllowed } = useContext(AuthContext);
  const processedDataRef = useRef(null);

  const formik = useFormik({
    initialValues: patSearchParameters,
    validationSchema,
    onSubmit: (values) => {
      setRows([]);
      dispatch(patSearchActions.salvarEstado(values));

      const params = {
        NuPat: values.NuPat,
        NuProtocolo: values.nuProtocolo,
        NuOs: values.nuOs,
        Identificador: values.identificador,
        UrtProcessante: values.urtProcessante,
        SqSituacao: values.sqSituacao,
        MotivoBaixa: values.motivoBaixa,
        DetalheBaixa: values.detalheBaixa,
        DtInicio: values.dtInicio ? values.dtInicio.split("-").reverse().join("/") : "",
        DtFim: values.dtFim ? values.dtFim.split("-").reverse().join("/") : "",
      };
      requestForm(apiRoutes.fiscalizacao.pat.consultarPat, params);
    },
  });

  const { setSubmitting, setErrors, errors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) setSubmitting(false);

    if (errorForm && isValid && !isLoading) setErrors({ ...errors, ...errorForm });

    if (dataForm?.result && !isLoading && dataForm !== processedDataRef.current) {
      processedDataRef.current = dataForm;
      if (!dataForm.result.length) {
        toast.info("Não há nenhum item a ser exibido com o critério de seleção", { toastId: "pat-not-found" });
        return;
      }

      const results = dataForm.result || [];
      setRows(results);
      dispatch(patSearchActions.salvarResultados(results));
    }
  }, [setSubmitting, setErrors, errors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  // Resetar pesquisa quando entra na tela vindo de fora (menu), preservar quando volta do MomentoCiencia (goBack)
  useEffect(() => {
    if (history.action !== "POP") {
      dispatch(patSearchActions.reset());
      setRows([]);
      formik.resetForm({
        values: {
          NuPat: "",
          nuProtocolo: "",
          nuOs: "",
          identificador: "",
          urtProcessante: "",
          sqSituacao: "",
          motivoBaixa: "",
          detalheBaixa: "",
          dtInicio: "",
          dtFim: "",
        },
      });
    }
  }, []);

  useEffect(() => {
    requestUrt(apiRoutes.fiscalizacao.pat.listaOrgaoUrt);
  }, [requestUrt]);

  useEffect(() => {
    if (dataUrt?.result) {
      const options = [
        { key: "0", value: "", text: "Selecione" },
        ...dataUrt.result.map((item, index) => ({
          key: String(index + 1),
          value: String(item.sqOrgao),
          text: `${item.sgOrgao} - ${item.nmOrgao}`,
        })),
      ];
      setUrtOptions(options);
    }
  }, [dataUrt]);

  useEffect(() => {
    const { state } = location;

    if (state !== undefined) {
      const { codigo, tipo, orgao } = state;

      const objeto = JSON.parse(localStorage.getItem("user"));
      const { cnpjCpf } = objeto;
      const params = {
        codigo: codigo || "0",
        tipo,
        orgao: orgao || "0",
        codUsuario: cnpjCpf,
        permissao: isAllowed(funcionalidades.os.visaoGerencial) ? 1 : 2,
      };
      requestForm(apiRoutes.fiscalizacao.ordemservico.consultarOSInformacaoSintetica, params);
    }
  }, [location, isAllowed, requestForm]);

  function resetForm() {
    formik.resetForm({
      values: {
        NuPat: "",
        nuProtocolo: "",
        nuOs: "",
        identificador: "",
        urtProcessante: "",
        sqSituacao: "",
        motivoBaixa: "",
        detalheBaixa: "",
        dtInicio: "",
        dtFim: "",
      },
    });
    setRows([]);
    dispatch(patSearchActions.reset());
  }

  const hasFilter = Object.values(formik.values).some((v) => v !== "");
  const isBaixado = formik.values.sqSituacao === "B";
  const detalheOptions = detalheBaixaMap[formik.values.motivoBaixa] || [];

  useEffect(() => {
    if (formik.values.sqSituacao !== "B") {
      formik.setFieldValue("motivoBaixa", "");
      formik.setFieldValue("detalheBaixa", "");
    }
  }, [formik.values.sqSituacao]);

  useEffect(() => {
    formik.setFieldValue("detalheBaixa", "");
  }, [formik.values.motivoBaixa]);

  function isExecutor(row) {
    const executor = row.executores?.filter((exec) => exec.includes(user.nome)).length > 0;
    const rota = "consultarPat/detalhar";

    if (isAllowed(funcionalidades.os.visaoGerencial) || isAllowed(funcionalidades.os.visaoAuditor)) {
      if (isAllowed(funcionalidades.os.visaoGerencial)) {
        history.push({
          pathname: rota,
          state: { itemKey: row.codigo },
        });
      } else if (isAllowed(funcionalidades.os.visaoAuditor)) {
        if (executor && row.dataCiencia === null && funcionalidades.os.visaoAuditor) {
          toast.warning("Atenção! Para prosseguir é necessário que seja registrada a sua ciência");
        } else if (!executor && isAllowed(funcionalidades.os.visaoAuditor)) {
          toast.warning("Usuário não é executor da OS");
        } else {
          history.push({
            pathname: rota,
            state: { itemKey: row.codigo },
          });
        }
      }
    } else {
      toast.warning("Usuário não tem permissão Gerencial ou de Auditor");
    }
  }

  // Mapeamento de colunas: chave JSON -> nome da coluna na planilha
  const columnMap = {
    cdProcesso: "Nº PAT",
    nmFormal: "Razão Social",
    nuIe: "IE",
  };

  const handleExport = (data) => {
    const listaData = data.map((x) => ({
      ...x,
      dtInclusao: formatarData(x.dtInclusao, "dd/MM/yyyy HH:mm"),
      dataEncerramento: formatarData(x.dataEncerramento, "dd/MM/yyyy HH:mm"),
    }));
  };
  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Consultar PAT</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="font-weight-bold text-primary flex-grow-1 ml-2 mt-2">Consulta de PAT</h6>
              <div className="card-body">
                <FormAlert formik={formik} data={dataForm} errors={errorForm} isLoading={isLoading} />
                <Form noValidate onSubmit={formik.handleSubmit}>
                  <Row>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Nº PAT</Form.Label>
                      <TextInputFormik placeholder="Informe o número do PAT" formik={formik} field="NuPat" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Nº Protocolo</Form.Label>
                      <TextInputFormik
                        placeholder="Informe o número do protocolo"
                        formik={formik}
                        field="nuProtocolo"
                      />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Nº OS</Form.Label>
                      <TextInputFormik placeholder="Informe o número da OS" formik={formik} field="nuOs" />
                    </Form.Group>
                  </Row>
                  <Row className="mt-2">
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>IE / CNPJ / CPF</Form.Label>
                      <TextInputFormik
                        placeholder="Informe um IE ou um CNPJ ou um CPF"
                        formik={formik}
                        field="identificador"
                      />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>URT Processante</Form.Label>
                      <SelectInput formik={formik} field="urtProcessante" options={urtOptions} />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Situação</Form.Label>
                      <SelectInput formik={formik} field="sqSituacao" options={situacaoPat} />
                    </Form.Group>
                  </Row>
                  {isBaixado && (
                    <Row className="mt-2">
                      <Form.Group as={Col} sm={12} md={4}>
                        <Form.Label>Motivo de Baixa</Form.Label>
                        <SelectInput formik={formik} field="motivoBaixa" options={motivoBaixaOptions} />
                      </Form.Group>
                      {formik.values.motivoBaixa && detalheOptions.length > 0 && (
                        <Form.Group as={Col} sm={12} md={4}>
                          <Form.Label>Detalhe da Baixa</Form.Label>
                          <SelectInput formik={formik} field="detalheBaixa" options={detalheOptions} />
                        </Form.Group>
                      )}
                    </Row>
                  )}
                  <Row className="mt-2">
                    <Form.Group className="form-group-periodos" as={Col} sm={12} md={4}>
                      <Form.Label>Período de Inclusão</Form.Label>
                      <div className="form-inline">
                        <DateTimeInputFormik formik={formik} field="dtInicio" />
                        <Form.Label className="mx-3"> a </Form.Label>
                        <DateTimeInputFormik formik={formik} field="dtFim" />
                      </div>
                    </Form.Group>
                  </Row>
                  <Row className="mt-2">
                    <Form.Group as={Row} style={{ marginLeft: 0 }} className="d-flex flex-row">
                      <Col sm="12">
                        <Button className="btn btn-primary btn-md mr-2" type="submit" disabled={!hasFilter}>
                          Buscar
                        </Button>
                        <Button
                          className="btn btn-primary btn-md mr-2"
                          variant="light"
                          onClick={() => {
                            resetForm();
                          }}
                        >
                          Limpar
                        </Button>
                      </Col>
                    </Form.Group>
                  </Row>
                </Form>
              </div>
            </div>

            <div className="col-lg-12">
              <div className="card-body white">
                <DataTable
                  noHeader
                  keyField="sqProcesso"
                  columns={columns}
                  data={rows}
                  progressPending={isLoading}
                  noDataComponent="Nenhum registro para exibir"
                  progressComponent={<TextSpinner />}
                  pointerOnHover
                  sortable
                  sortIcon={<BsArrowDown />}
                  expandableRows
                  expandableRowsComponent={PatDetalhesExpandido}
                  expandOnRowClicked
                  pagination
                  paginationComponentOptions={constants.paginationComponentOptions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsolidacaoDebitosFiscais;
